/**
 * Redux Store Configuration
 * Configures the Redux store with RTK Query middleware
 */

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { baseApi } from './api/baseApi';
import { tenancyAgreementsApi } from './api/tenancyAgreementsApi';

/**
 * Configure the Redux store
 */
export const store = configureStore({
  reducer: {
    // Add the base API reducer
    [baseApi.reducerPath]: baseApi.reducer,
    // Add the tenancy agreements API reducer
    [tenancyAgreementsApi.reducerPath]: tenancyAgreementsApi.reducer,
  },
  // Adding the api middleware enables caching, invalidation, polling, and other features of RTK Query
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore Date objects in Redux state (they're serialized automatically)
        // Ignore Request objects in RTK Query metadata (they're not serializable)
        ignoredActions: [
          'api/executeQuery/fulfilled',
          'api/executeQuery/rejected',
          'api/executeMutation/fulfilled',
          'api/executeMutation/rejected',
        ],
        ignoredActionPaths: [
          'meta.arg',
          'meta.baseQueryMeta.request', // Request object from fetch
          'meta.baseQueryMeta.response', // Response object from fetch
          'payload.createdAt',
          'payload.updatedAt',
        ],
        ignoredPaths: ['api.queries', 'api.mutations'],
      },
    }).concat(baseApi.middleware, tenancyAgreementsApi.middleware),
  devTools: import.meta.env.MODE !== 'production',
});

// Enable refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);

// Export types for TypeScript
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
